import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

type Options = {
  dragThresholdPx?: number;
  translateVerticalWheel?: boolean;
  enableOnTouch?: boolean;
};

type DragState = {
  isPrimed: boolean;
  isActive: boolean;
  startClientX: number;
  lastClientX: number;
  hasMoved: boolean;
};

export const useDragHorizontalScroll = <T extends HTMLElement>(
  options?: Options
) => {
  const {
    dragThresholdPx = 6,
    translateVerticalWheel = true,
    enableOnTouch = false,
  } = options ?? {};

  const viewportRef = useRef<T | null>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);

  const dragStateRef = useRef<DragState>({
    isPrimed: false,
    isActive: false,
    startClientX: 0,
    lastClientX: 0,
    hasMoved: false,
  });

  const computeIsScrollable = useCallback(() => {
    const viewportElement = viewportRef.current;
    if (!viewportElement) {
      return false;
    }

    const trackElement =
      viewportElement.firstElementChild as HTMLElement | null;

    const contentWidth =
      trackElement?.scrollWidth ?? viewportElement.scrollWidth;
    const viewportWidth = viewportElement.clientWidth;

    return contentWidth > viewportWidth;
  }, []);

  const updateScrollability = useCallback(() => {
    const value = computeIsScrollable();
    setIsScrollable(value);
  }, [computeIsScrollable]);

  const handlePointerDownCapture = useCallback(
    (event: React.PointerEvent<T>) => {
      const viewportElement = viewportRef.current;
      if (!viewportElement) {
        return;
      }

      const isMouseOrPen =
        event.pointerType === "mouse" || event.pointerType === "pen";
      if (!isMouseOrPen && !enableOnTouch) {
        return;
      }

      if (event.button !== 0) {
        return;
      }

      if (!computeIsScrollable()) {
        return;
      }

      dragStateRef.current.isPrimed = true;
      dragStateRef.current.isActive = false;
      dragStateRef.current.startClientX = event.clientX;
      dragStateRef.current.lastClientX = event.clientX;
      dragStateRef.current.hasMoved = false;
    },
    [computeIsScrollable, enableOnTouch]
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<T>) => {
      const viewportElement = viewportRef.current;
      if (!viewportElement) {
        return;
      }

      if (dragStateRef.current.isPrimed && !dragStateRef.current.isActive) {
        const distanceFromStart = Math.abs(
          event.clientX - dragStateRef.current.startClientX
        );
        if (distanceFromStart >= dragThresholdPx) {
          try {
            viewportElement.setPointerCapture(event.pointerId);
          } catch {
            // ignore if unsupported
          }
          dragStateRef.current.isActive = true;
          dragStateRef.current.hasMoved = true;
          setIsDragging(true);
        } else {
          return;
        }
      }

      if (!dragStateRef.current.isActive) {
        return;
      }

      const deltaX = event.clientX - dragStateRef.current.lastClientX;
      viewportElement.scrollLeft -= deltaX;
      dragStateRef.current.lastClientX = event.clientX;
    },
    [dragThresholdPx]
  );

  const handlePointerEnd = useCallback((event?: React.PointerEvent<T>) => {
    const viewportElement = viewportRef.current;
    if (!viewportElement) {
      return;
    }

    if (event && event.pointerId) {
      try {
        if (viewportElement.hasPointerCapture(event.pointerId)) {
          viewportElement.releasePointerCapture(event.pointerId);
        }
      } catch {
        // ignore
      }
    }

    dragStateRef.current.isPrimed = false;
    dragStateRef.current.isActive = false;
    setIsDragging(false);
  }, []);

  const handleClickCapture = useCallback((event: React.MouseEvent) => {
    if (dragStateRef.current.hasMoved) {
      event.stopPropagation();
      event.preventDefault();
    }
    dragStateRef.current.hasMoved = false;
  }, []);

  const handleDragStartCapture = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleWheel = useCallback(
    (event: React.WheelEvent<T>) => {
      if (!translateVerticalWheel) {
        return;
      }

      const viewportElement = viewportRef.current;
      if (!viewportElement) {
        return;
      }

      if (!computeIsScrollable()) {
        return;
      }

      if (event.deltaY !== 0 && event.shiftKey === false) {
        viewportElement.scrollLeft += event.deltaY;
        event.preventDefault();
      }
    },
    [computeIsScrollable, translateVerticalWheel]
  );

  // Synchronous initial measurement to avoid first-render false negatives
  useLayoutEffect(() => {
    updateScrollability();

    // Also re-measure on next frame after layout settles
    const rafId = requestAnimationFrame(() => {
      updateScrollability();
    });

    // If fonts impact layout, re-measure after fonts load
    if (
      (document as any).fonts &&
      typeof (document as any).fonts.ready?.then === "function"
    ) {
      (document as any).fonts.ready.then(() => {
        updateScrollability();
      });
    }

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [updateScrollability]);

  // Observe size and DOM mutations for ongoing accuracy
  useEffect(() => {
    const viewportElement = viewportRef.current;
    if (!viewportElement) {
      return undefined;
    }

    const trackElement =
      viewportElement.firstElementChild as HTMLElement | null;

    const resizeObserver = new ResizeObserver(() => {
      updateScrollability();
    });
    resizeObserver.observe(viewportElement);
    if (trackElement) {
      resizeObserver.observe(trackElement);
    }

    const mutationObserver = new MutationObserver(() => {
      // Re-attach observer to a new track if it appears later
      const currentTrack = viewportRef.current
        ?.firstElementChild as HTMLElement | null;
      if (currentTrack) {
        try {
          resizeObserver.observe(currentTrack);
        } catch {
          // ignore if already observed
        }
      }
      updateScrollability();
    });
    mutationObserver.observe(viewportElement, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });

    const handleWindowResize = () => {
      updateScrollability();
    };
    window.addEventListener("resize", handleWindowResize);

    return () => {
      window.removeEventListener("resize", handleWindowResize);
      mutationObserver.disconnect();
      resizeObserver.disconnect();
      dragStateRef.current.isPrimed = false;
      dragStateRef.current.isActive = false;
      dragStateRef.current.hasMoved = false;
      setIsDragging(false);
    };
  }, [updateScrollability]);

  // Imperative recalc for callers when data changes
  const recalculate = useCallback(() => {
    updateScrollability();
  }, [updateScrollability]);

  return {
    ref: viewportRef,
    isDragging,
    isScrollable,
    recalculate,
    handlers: {
      onPointerDownCapture: handlePointerDownCapture,
      onPointerMove: handlePointerMove,
      onPointerUp: handlePointerEnd,
      onPointerLeave: handlePointerEnd,
      onClickCapture: handleClickCapture,
      onDragStartCapture: handleDragStartCapture,
      onWheel: handleWheel,
    },
  };
};

export default useDragHorizontalScroll;
